import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Typography, Alert, Spin } from 'antd';
import {
  MobileOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { deviceAPI, policyAPI, categoryAPI } from '../utils/api';
import { Device, DeviceStats, Policy, Category } from '../types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);


const { Title } = Typography;

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DeviceStats | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, devicesData, policiesData, categoriesData] = await Promise.all([
        deviceAPI.getStats(),
        deviceAPI.getDevices(),
        policyAPI.getPolicies(),
        categoryAPI.getCategoryTree()
      ]);

      setStats(statsData);
      setDevices(devicesData.slice(0, 10)); // 只显示最近的10个设备
      setPolicies(policiesData);
      setCategories(categoriesData);
    } catch (err: any) {
      setError(err.message || '获取数据失败');
      console.error('获取数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 获取设备有效策略的 queryPolicyInterval（秒），优先设备策略，否则从目录继承
  const getDeviceQueryInterval = (device: Device): number => {
    // 优先使用设备自身的策略
    let policyId = device.policyId;
    
    // 如果设备没有策略，尝试从目录链向上查找
    if (!policyId && device.categoryId) {
      const findPolicyFromCategory = (catId: number): number | undefined => {
        const category = categories.find(c => c.id === catId);
        if (!category) return undefined;
        if (category.policyId) return category.policyId;
        if (category.parentId) return findPolicyFromCategory(category.parentId);
        return undefined;
      };
      policyId = findPolicyFromCategory(device.categoryId);
    }
    
    if (policyId) {
      const policy = policies.find(p => p.id === policyId);
      if (policy?.value) {
        try {
          const parsed = JSON.parse(policy.value);
          const interval = parseInt(parsed?.strategies?.queryPolicyInterval, 10);
          if (!isNaN(interval) && interval > 0) {
            return interval;
          }
        } catch (e) {
          // JSON 解析失败，使用默认值
        }
      }
    }
    
    return 600; // 默认 600 秒
  };

  // 获取设备状态
  // 规则：
  // - 在线：当前时间 < 最后通信时间 + 策略查询间隔
  // - 最近活跃：24小时内（但不满足在线条件）
  // - 离线：超过24小时
  // - 已禁用：device.disabled = true
  const getDeviceStatus = (device: Device) => {
    if (device.disabled) {
      return <Tag color="red">已禁用</Tag>;
    }

    if (device.lastCommunicationTime) {
      const lastCommLocal = dayjs.utc(device.lastCommunicationTime).local();
      const now = dayjs();
      const queryIntervalSeconds = getDeviceQueryInterval(device);
      const expectedNextComm = lastCommLocal.add(queryIntervalSeconds, 'second');
      
      // 在线：当前时间 < 最后通信时间 + 策略查询间隔
      if (now.isBefore(expectedNextComm)) {
        return <Tag color="green">在线</Tag>;
      }
      
      // 最近活跃：24小时内
      const hoursSinceComm = now.diff(lastCommLocal, 'hour');
      if (hoursSinceComm < 24) {
        return <Tag color="orange">最近活跃</Tag>;
      }
    }

    return <Tag color="red">离线</Tag>;
  };

  const columns = [
    {
      title: '设备代码',
      dataIndex: 'deviceCode',
      key: 'deviceCode',
    },
    {
      title: '用户身份',
      dataIndex: 'userIdentity',
      key: 'userIdentity',
      render: (text: string) => text || '-',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: '状态',
      key: 'status',
      render: (_: any, record: Device) => getDeviceStatus(record),
    },
    {
      title: '最后通信时间',
      dataIndex: 'lastCommunicationTime',
      key: 'lastCommunicationTime',
      render: (text: string) => text ? dayjs.utc(text).local().format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={2} style={{ margin: 0 }}>
          📊 仪表盘
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666' }}>
          设备管理系统概览
        </p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={4}>
          <Card size="small" styles={{ body: { padding: '12px' } }}>
            <Statistic
              title="设备总数"
              value={stats?.total || 0}
              prefix={<MobileOutlined />}
              valueStyle={{ color: '#1890ff' }}
              className="stats-card"
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={4}>
          <Card size="small" styles={{ body: { padding: '12px' } }}>
            <Statistic
              title="在线"
              value={stats?.online || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              className="stats-card"
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={4}>
          <Card size="small" styles={{ body: { padding: '12px' } }}>
            <Statistic
              title="最近活跃"
              value={stats?.recent || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
              className="stats-card"
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={4}>
          <Card size="small" styles={{ body: { padding: '12px' } }}>
            <Statistic
              title="离线"
              value={stats?.offline || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#999' }}
              className="stats-card"
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={4}>
          <Card size="small" styles={{ body: { padding: '12px' } }}>
            <Statistic
              title="已禁用"
              value={stats?.disabled || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
              className="stats-card"
            />
          </Card>
        </Col>
      </Row>

      {/* 最近设备列表 */}
      <Card
        title="最近设备"
        className="content-card"
        extra={<span style={{ color: '#666', fontSize: 12 }}>最近注册的设备</span>}
      >
        <div className="mobile-table-wrapper">
          <Table
            columns={columns}
            dataSource={devices}
            rowKey="id"
            pagination={false}
            className="device-table"
            scroll={{ x: 600 }}
            size="small"
          />
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;
